import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "react-bootstrap";

// The sections of the customer trip page, in page order. Each is found by its data-tour mark.
export const TRIP_PAGE_TOUR_STEPS = [
  {
    target: "driver-location",
    title: "Trip Map",
    text: "Every place of your trip and your stay for each night, with your vehicle's live position during the trip. Tap a day to see only that day, or tap a pin for its times.",
  },
  {
    target: "trip-plan",
    title: "Trip Plan",
    text: "The day you picked on the map, place by place. Set your pickup point and time in the first row of Day 1. When your driver reaches or leaves a place, the time appears next to it.",
  },
  {
    target: "emergency",
    title: "Emergency Contact",
    text: "Need urgent help during your trip? Call or WhatsApp the person shown here. Only the team member available right now is listed.",
  },
  {
    target: "id-cards",
    title: "Your ID Card",
    text: "Upload your Aadhaar or another government photo ID here. You can add the front and back, or one for each traveller.",
  },
  {
    target: "payment",
    title: "Payment Schedule",
    text: "What you have paid and what is left: one payment right after you board the vehicle, and the rest on Day 1 after hotel check-in.",
  },
  {
    target: "trip-info",
    title: "Driver and Vehicle",
    text: "Your vehicle pickup and drop times, your driver's name and phone number, and the vehicle details. Driver and vehicle appear once they are assigned.",
  },
  {
    target: "hotels",
    title: "Day Wise Stay",
    text: "Where you stay each night of the trip.",
  },
  {
    target: "page-actions",
    title: "Refresh and Share",
    text: "Tap Refresh for the latest updates, or Copy Link to share this page with your family. Page Guide shows this tour again.",
  },
];

// Before the trip policy is accepted the page shows only these.
export const POLICY_PAGE_TOUR_STEPS = [
  {
    target: "payment",
    title: "Payment Schedule",
    text: "What you have paid and what is left: one payment right after you board the vehicle, and the rest on Day 1 after hotel check-in.",
  },
  {
    target: "policy-pickup",
    title: "Vehicle Pickup & Drop",
    text: "When the vehicle picks you up and when it drops you back.",
  },
  {
    target: "policy-locked",
    title: "Your Trip Details",
    text: "Your driver and vehicle, day wise stay and trip plan open here once you accept the trip policy below.",
  },
  {
    target: "policy-accept",
    title: "Accept the Trip Policy",
    text: "Read the points, tick the box, enter your name and allow location. Then tap Accept to open your full trip details.",
  },
  {
    target: "page-actions",
    title: "Refresh and Share",
    text: "Tap Refresh for the latest updates, or Copy Link to share this page with your family. Page Guide shows this tour again.",
  },
];

const PAD = 8;

function findTarget(target) {
  return document.querySelector(`[data-tour="${target}"]`);
}

function isShown(element) {
  return Boolean(element && element.getBoundingClientRect().height > 0);
}

/**
 * Walks a first-time visitor through the page one section at a time: the section is lit up, the
 * rest of the page dimmed, and a card explains it with Back / Next / Skip. Sections a booking does
 * not have yet (no driver location before the trip, say) are left out.
 */
export default function TrackingPageTour({ steps = TRIP_PAGE_TOUR_STEPS, open, onClose }) {
  const [active, setActive] = useState([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!open) return;
    const present = steps.filter((step) => isShown(findTarget(step.target)));
    setActive(present);
    setIndex(0);
    if (!present.length) onClose();
  }, [open, steps, onClose]);

  const step = open ? active[index] : null;

  const measure = useCallback(() => {
    const element = step ? findTarget(step.target) : null;
    const next = element ? element.getBoundingClientRect() : null;
    setRect((current) => {
      if (!next) return null;
      if (current && current.top === next.top && current.left === next.left
          && current.width === next.width && current.height === next.height) return current;
      return { top: next.top, left: next.left, width: next.width, height: next.height };
    });
  }, [step]);

  useEffect(() => {
    if (!step) return undefined;
    findTarget(step.target)?.scrollIntoView({ behavior: "smooth", block: "center" });
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    // Smooth scrolling and a map loading its tiles move the section after the first measure.
    const timer = setInterval(measure, 200);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      clearInterval(timer);
    };
  }, [step, measure]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!step) return null;
  const last = index === active.length - 1;
  const viewportHeight = window.innerHeight || 0;
  // Keep the card away from the section it explains.
  const cardAtTop = rect ? rect.top + rect.height / 2 > viewportHeight / 2 : false;

  return createPortal(
    <div role="dialog" aria-modal="true" aria-label="Page guide">
      <div className={`trip-tour-blocker${rect ? "" : " trip-tour-blocker-dim"}`} />
      {rect && (
        <div
          className="trip-tour-highlight"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
          }}
        />
      )}
      <div className={`trip-tour-card${cardAtTop ? " trip-tour-card-top" : ""}`}>
        <div className="trip-tour-step">Step {index + 1} of {active.length}</div>
        <div className="trip-tour-title">{step.title}</div>
        <div className="trip-tour-text">{step.text}</div>
        <div className="trip-tour-actions">
          <Button variant="link" size="sm" onClick={onClose}>Skip</Button>
          <div className="d-flex gap-2">
            {index > 0 && (
              <Button variant="outline-secondary" size="sm" onClick={() => setIndex(index - 1)}>Back</Button>
            )}
            <Button size="sm" onClick={() => (last ? onClose() : setIndex(index + 1))}>{last ? "Done" : "Next"}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
